import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUsersApiService } from '../../../../apis/users/useUsersApiService';
import {
  ParentUserParams,
  CreateParentUserRequest,
  CreateMeetingRequest,
} from '../../../../apis/users/users.api.interfaces';
import { getParentListKey } from './useParentListHook';
import { getParentsSummaryKey } from './useParentsSummaryHook';

// Query Keys
export const PARENT_USERS_QUERY_KEY = 'parent-users';
export const PARENT_USER_DETAIL_QUERY_KEY = 'parent-user-detail';
export const PARENT_USERS_SUMMARY_QUERY_KEY = 'parent-users-summary';

export const useParentsList = (params: ParentUserParams, enabled = true) => {
  const { getParentUsers } = useUsersApiService();

  return useQuery({
    queryKey: [PARENT_USERS_QUERY_KEY, params],
    queryFn: () => getParentUsers(params),
    enabled,
  });
};

export const useParentUserById = (id: string, enabled = true) => {
  const { getParentUserById } = useUsersApiService();

  return useQuery({
    queryKey: [PARENT_USER_DETAIL_QUERY_KEY, id],
    queryFn: () => getParentUserById(id),
    enabled: enabled && !!id,
  });
};

export const useParentsSummary = (instituteId?: string, enabled = true) => {
  const { getParentsSummary } = useUsersApiService();

  return useQuery({
    queryKey: [PARENT_USERS_SUMMARY_QUERY_KEY, instituteId],
    queryFn: () => getParentsSummary(instituteId),
    enabled,
  });
};

export const useCreateParentUser = () => {
  const queryClient = useQueryClient();
  const { createParentUser } = useUsersApiService();

  return useMutation({
    mutationFn: (data: CreateParentUserRequest) => createParentUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PARENT_USERS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [PARENT_USERS_SUMMARY_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [getParentListKey] });
      queryClient.invalidateQueries({ queryKey: [getParentsSummaryKey] });
    },
  });
};

export const useUpdateParentUser = () => {
  const queryClient = useQueryClient();
  const { updateParentUser } = useUsersApiService();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreateParentUserRequest>;
    }) => updateParentUser(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [PARENT_USERS_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [PARENT_USER_DETAIL_QUERY_KEY, variables.id],
      });
      queryClient.invalidateQueries({ queryKey: [PARENT_USERS_SUMMARY_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [getParentListKey] });
      queryClient.invalidateQueries({ queryKey: [getParentsSummaryKey] });
    },
  });
};

export const useDeleteParentUser = () => {
  const queryClient = useQueryClient();
  const { deleteParentUser } = useUsersApiService();

  return useMutation({
    mutationFn: (id: string) => deleteParentUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PARENT_USERS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [PARENT_USERS_SUMMARY_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [getParentListKey] });
      queryClient.invalidateQueries({ queryKey: [getParentsSummaryKey] });
    },
  });
};

export const useAddMeetingToParent = () => {
  const queryClient = useQueryClient();
  const { addMeetingToParent } = useUsersApiService();

  return useMutation({
    mutationFn: ({
      id,
      meeting,
    }: {
      id: string;
      meeting: CreateMeetingRequest;
    }) => addMeetingToParent(id, meeting),
    onSuccess: (_, variables) => {
      // Invalidate specific parent user detail to refresh meeting history
      queryClient.invalidateQueries({
        queryKey: [PARENT_USER_DETAIL_QUERY_KEY, variables.id],
      });
    },
  });
};
